import { ctaCreateId, searchId } from "@/assets/googleTrackIds";
import { depositApis } from "@payping/api";
import type { DepositIdentifierDetails } from "@payping/api";
import { getCashOutDeposit } from "@payping/redux/actions";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { submitQueryParams } from "@payping/utils/query-string";
import { addOverflow } from "@payping/utils/string";
import {
  Button,
  Copy,
  DataHeader,
  Input,
  LegacyForm,
  Modal,
  Space,
  Table,
  TableAction,
  Tooltip,
  Typography,
  useMessagedAsyncRetry,
  usePaypingContext,
} from "@payping/web-components";
import type { ColumnsType, TablePaginationConfig } from "@payping/web-components/components/table/types";
import qs from "query-string";
import React, { ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsync } from "react-use";

import { Add } from "@payping/icons";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { FiltersType, GridifyConditionEnum, createQuery } from "@payping/utils/api";
import styles from "@/assets/scss/Settlement/index.module.scss";
import { ListStringQueryParamsType } from "@/pages/Settlement/ID/types";
import { DashboardRoutes } from "@/router";
import { ListNumberQueryParamsType } from "../../Report/Request/types";
import { IDDetails } from "../Details";

const List: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const parsedSearch = qs.parse(location.search);
  const { pageSize = 10, pageNumber = 1 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const { searchText }: ListStringQueryParamsType = qs.parse(location.search);
  const deposits = useAppSelector((state) => state.cashOuts.depositList);
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [, contextHolder] = Modal.useModal();
  const [rowDetail, setRowDetail] = useState<DepositIdentifierDetails>();
  const [form] = LegacyForm.useForm<ListStringQueryParamsType>();
  const parsed = qs.parse(location.search);
  const { breakpoints } = usePaypingContext();
  const { xl } = breakpoints;
  const { loading, retry } = useMessagedAsyncRetry(
    async () => {
      const filter: FiltersType = [
        { depositId: [GridifyConditionEnum.Equal, searchText] },
        // GridifyLogicalEnum.Or,
        // { fullName: [GridifyConditionEnum.Contains, searchText] },
      ];
      await dispatch(getCashOutDeposit(pageNumber, pageSize, undefined, createQuery(filter)));
    },
    [dispatch, pageSize, pageNumber, searchText],
    {
      success: false,
      useProgress: true,
    },
  );

  const columns: ColumnsType<DepositIdentifierDetails> = [
    {
      dataIndex: "title",
      ellipsis: true,
      key: "title",

      render(_, record: DepositIdentifierDetails) {
        return (
          <Tooltip title={record.isLegal ? record.title : `${record.firstName} ${record.lastName}`}>
            <span>
              {record.isLegal
                ? addOverflow(record.title || "", 30)
                : addOverflow(`${record.firstName} ${record.lastName}`, 30)}
            </span>
          </Tooltip>
        );
      },
      title: t("settlement.id.payer"),
    },
    {
      dataIndex: "depositId",
      key: "depositId",
      render(_, record: DepositIdentifierDetails) {
        return <Copy text={record.depositId || ""}>{record.depositId}</Copy>;
      },
      title: t("settlement.id.code"),
    },
    {
      dataIndex: "description",
      ellipsis: true,
      key: "description",
      render(value?: string) {
        return (
          <Tooltip title={value}>
            <span>{addOverflow(value || "-", 30)}</span>
          </Tooltip>
        );
      },
      title: t("description"),
    },
    {
      dataIndex: "createdOn",
      key: "createdOn",
      render(value: string) {
        return <span>{toJalali({ date: value, toFormat: dateFormats.ui.jalaliDateTime }).string}</span>;
      },
      title: t("settlement.id.createDate"),
    },
  ];

  const actions = (_: unknown, record?: DepositIdentifierDetails): ReactNode[] => [
    <a key={0}>
      <TableAction
        iconColor={"primary"}
        label={t("details")}
        onClick={() => {
          setRowDetail(record);
        }}
        key={1}
      />
    </a>,
    <a key={1}>
      <TableAction
        iconColor={"primary"}
        label={t("settlement.id.paymentHistory")}
        onClick={() => {
          navigate({ pathname: DashboardRoutes.settlement.transaction.url, search: `depositId=${record?.depositId}` });
        }}
        key={2}
      />
    </a>,
  ];

  const pagination: TablePaginationConfig = {
    onChange: (page, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: page,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    onShowSizeChange: (_currentPage, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: 1,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    pageNumber,
    pageSize,
    showTotal: (total, range): string => t("showTotal", { range: `${range[0]}-${range[1]}`, total }),
    total: deposits?.count,
  };

  const { value: accountData } = useAsync(() => depositApis.getDepositIbanGet(), []);

  return (
    <LegacyForm
      onFinish={(values) => {
        const newValues = {
          searchText: values.searchText,
        };
        submitQueryParams({
          navigate,
          newValues,
          parsedSearch: parsed,
          pathname: location.pathname,
          retry,
        });
      }}
      form={form}
      className={"tw:w-full"}
      layout={"vertical"}
    >
      <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
        <DataHeader
          title={<Typography.Title level={4}>{t("settlement.id.title")}</Typography.Title>}
          primaryAction={
            <Button
              key={0}
              type={"primary"}
              icon={<Add width={14} height={14} />}
              onClick={(): void =>
                navigate(
                  {
                    pathname: DashboardRoutes.settlement.createId.url,
                  },
                  { state: { locationSearch: location.search } },
                )
              }
              data-google-track={ctaCreateId}
            >
              {t("settlement.id.create")}
            </Button>
          }
          filters={{
            searchbarInput: (
              <LegacyForm.Item name={"searchText"} noStyle>
                <Input.Search
                  className={xl ? "tw:w-93.75" : undefined}
                  allowClear
                  placeholder={t("search.search")}
                  onSearch={() => form.submit()}
                  data-google-track={searchId}
                />
              </LegacyForm.Item>
            ),
          }}
        />

        <Table<DepositIdentifierDetails>
          actions={actions}
          hasIdColumn
          size={"large"}
          className={styles.tableWrapper}
          dataSource={deposits?.data}
          columns={columns}
          pagination={pagination}
          loading={loading}
        />
      </Space>

      <IDDetails data={rowDetail} setData={setRowDetail} accountData={accountData} />
      {contextHolder}
    </LegacyForm>
  );
};

export default List;
